import { useCurrency } from '../contexts/CurrencyContext';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../firebaseConfig';
import {
  collection, query, where, getDocs, doc, setDoc,
  updateDoc, increment, getDoc
} from 'firebase/firestore';
import QRCode from 'react-qr-code';
import './CustomerPage.css';
import Logo from '../assets/loyaltap_logo.png';
import CustomerReview from './CustomerReview';
import ChatbotWidget from './ChatbotWidget';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import RetailRecordForm from './RetailRecordForm';
import RetailClaimForm from './RetailClaimForm';

const CustomerPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { t } = useTranslation();
  const { currency, currencyCode } = useCurrency();
  
  const [customer, setCustomer] = useState(null);
  const [offers, setOffers] = useState([]);
  const [offerData, setOfferData] = useState({});
  const [purchaseOfferId, setPurchaseOfferId] = useState('');
  const [claimOfferId, setClaimOfferId] = useState('');
  const [purchaseNumItems, setPurchaseNumItems] = useState(1);
  const [claimNumber, setClaimNumber] = useState(1);
  const [adminPin, setAdminPin] = useState('');
  const [claimAdminPin, setClaimAdminPin] = useState('');
  const [message, setMessage] = useState('');
  const [totalPoints, setTotalPoints] = useState(0);
  const [offerType, setOfferType] = useState('coffee');
  const coffeeShopId = localStorage.getItem('coffee_shop_id');

  useEffect(() => {
    const fetchData = async () => {
      const custSnap = await getDocs(query(collection(db, 'customers'), where('token', '==', token)));
      if (!custSnap.empty) {
        const custData = custSnap.docs[0].data();
        setCustomer({ id: custSnap.docs[0].id, ...custData });

        const offersSnap = await getDocs(query(collection(db, 'offers'), where('coffee_shop_id', '==', custData.coffee_shop_id), where('active', '==', true)));
        const offersArray = offersSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setOffers(offersArray);

        let data = {};

for (const offer of offersArray) {
  const docRef = doc(db, 'customer_offers', `${custSnap.docs[0].id}_${offer.id}`);
  const docSnap = await getDoc(docRef);
  const offerInfo = docSnap.exists()
    ? docSnap.data()
    : { num_items: 0, claimed_items: 0, retail_spent: 0 };

  data[offer.id] = offerInfo;
}

setOfferData(data);
setTotalPoints(custData.total_points || 0); // ✅ Use value stored in Firestore
      } else {
        setMessage(t('customer_not_found'));
      }
    };
    fetchData();
  }, [token, t]);

  const verifyAdminPin = async (pin) => {
    const adminSnap = await getDocs(query(collection(db, 'admin_users'), where('pin', '==', pin)));
    return !adminSnap.empty;
  };

  const handlePurchase = async (e) => {
    e.preventDefault();
    if (!(await verifyAdminPin(adminPin))) {
      setMessage(t('invalid_admin_pin'));
      return;
    }

    const offerRef = doc(db, 'customer_offers', `${customer.id}_${purchaseOfferId}`);
    await setDoc(
      offerRef,
      {
        customer_id: customer.id,
        offer_id: purchaseOfferId,
        num_items: increment(purchaseNumItems),
        claimed_items: increment(0),
      },
      { merge: true }
    );

    await updateDoc(doc(db, 'customers', customer.id), {
      total_points: increment(purchaseNumItems)
    });

    setMessage(t('purchase_success'));
    setOfferData(prev => ({
      ...prev,
      [purchaseOfferId]: {
        ...prev[purchaseOfferId],
        num_items: (prev[purchaseOfferId]?.num_items || 0) + purchaseNumItems,
      },
    }));
    setTotalPoints(prev => prev + purchaseNumItems);
    setPurchaseNumItems(1);
    setAdminPin('');
    setPurchaseOfferId('');
  };

  const handleClaim = async (e) => {
    e.preventDefault();
    if (!(await verifyAdminPin(claimAdminPin))) {
      setMessage(t('invalid_admin_pin'));
      return;
    }

    const offerDetails = offers.find(offer => offer.id === claimOfferId);
    if (!offerDetails) {
      setMessage(t('customer_not_found'));
      return;
    }

    const customerOfferRef = doc(db, 'customer_offers', `${customer.id}_${claimOfferId}`);
    const customerOfferSnap = await getDoc(customerOfferRef);
    const customerOfferData = customerOfferSnap.exists() ? customerOfferSnap.data() : { num_items: 0, claimed_items: 0 };

    const earnedRewards = Math.floor(customerOfferData.num_items / offerDetails.threshold) * offerDetails.reward;
    const unclaimedRewards = earnedRewards - customerOfferData.claimed_items;

    if (claimNumber <= 0 || claimNumber > unclaimedRewards) {
      setMessage(`${t('unclaimed')}: ${unclaimedRewards}`);
      return;
    }

    await updateDoc(customerOfferRef, { claimed_items: increment(claimNumber) });

    setMessage(t('claim_success', { count: claimNumber }));
    setOfferData(prev => ({
      ...prev,
      [claimOfferId]: {
        ...prev[claimOfferId],
        claimed_items: customerOfferData.claimed_items + claimNumber,
      },
    }));
    setClaimNumber(1);
    setClaimAdminPin('');
    setClaimOfferId('');
  };

  if (!customer) return <div className="loading">Loading...</div>;

  const filteredOffers = offers.filter(offer => offer.offer_type === offerType);

  return (
    <div className={`customer-page-wrapper ${theme}`}>
      <header className="customer-header">
        <img src={Logo} alt="LoyalTap" className="logo-image" />
        <h1 className='header-title'>{t('welcome')}, {customer.name}</h1>
        <p className="points-text">{t('loyalty_points')}: {totalPoints}</p>
        <button className="theme-toggle-btn" onClick={toggleTheme}>
          {theme === 'dark' ? t('light_mode') : t('dark_mode')}
        </button>
      </header>

      <div className="customer-scrollable-content">
        <div className="offer-type-switch">
        <button
    className={offerType === 'coffee' ? 'active' : ''}
    onClick={() => setOfferType('coffee')}
  >
    ☕ {t('offer_type.coffee')}
  </button>
  <button
    className={offerType === 'retail' ? 'active' : ''}
    onClick={() => setOfferType('retail')}
  >
    🛍️ {t('offer_type.retail')}
  </button>
        </div>

        {message && <div className="notification">{message}</div>}

        {offerType === 'coffee' && (
          <>
           <div className="offers-section">
  {filteredOffers.map((offer) => {
    const data = offerData[offer.id] || { num_items: 0, claimed_items: 0 };
    const earned = Math.floor((data.num_items || 0) / offer.threshold) * offer.reward;
    const unclaimed = earned - (data.claimed_items || 0);

    return (
      <div key={offer.id} className="offer-card">
        <h3>{offer.title}</h3>
        <p>{t('offer_description_placeholder')}: {offer.description}</p>
        <p>{t('threshold')}: {offer.threshold}</p>
        <p>{t('reward')}: {offer.reward}</p>
        <p><strong>{t('total_purchased')}: {data.num_items || 0}</strong></p>
        <p><strong>{t('claimed_rewards')}: {data.claimed_items || 0}</strong></p>
        <p><strong>{t('unclaimed')}: {unclaimed}</strong></p>
      </div>
    );
  })}
</div>

            <form className="form-section" onSubmit={handlePurchase}>
              <h2>{t('record_purchase')}</h2>
              <select value={purchaseOfferId} onChange={(e) => setPurchaseOfferId(e.target.value)} required>
                <option value="">{t('select_offer')}</option>
                {filteredOffers.map(offer => <option key={offer.id} value={offer.id}>{offer.title}</option>)}
              </select>
              <input type="number" min="1" value={purchaseNumItems} onChange={(e) => setPurchaseNumItems(Number(e.target.value))} />
              <input type="password" placeholder={t('admin_pin')} value={adminPin} onChange={(e) => setAdminPin(e.target.value)} />
              <button type="submit">{t('submit_purchase')}</button>
            </form>

            <form className="form-section" onSubmit={handleClaim}>
              <h2>{t('claim_reward')}</h2>
              <select value={claimOfferId} onChange={(e) => setClaimOfferId(e.target.value)} required>
                <option value="">{t('select_offer')}</option>
                {filteredOffers.map(offer => <option key={offer.id} value={offer.id}>{offer.title}</option>)}
              </select>
              <input type="number" min="1" value={claimNumber} onChange={(e) => setClaimNumber(Number(e.target.value))} />
              <input type="password" placeholder={t('admin_pin')} value={claimAdminPin} onChange={(e) => setClaimAdminPin(e.target.value)} />
              <button type="submit">{t('submit_claim')}</button>
            </form>
          </>
        )}

        {offerType === 'retail' && (
          <>
            <div className="offers-section">
            {filteredOffers.map((offer) => {
  const data = offerData[offer.id] || { retail_spent: 0, claimed_discounts: 0 };
  const spent = data.retail_spent || 0;
  const requirement = offer.min_requirement || 1;
  const discountVal = offer.discount_value || 0;
  const claimedValue = data.claimed_discounts || 0;

 // 💡 Calculate total earned discount
const totalEarned = offer.discount_type === 'fixed'
  ? Math.floor(spent / requirement) * discountVal
  : Math.floor(spent * discountVal / 100);

const unclaimed = Math.max(totalEarned - claimedValue, 0); // ✅ final value in €

return (
  <div key={offer.id} className="offer-card">
    {offer.image_url && <img src={offer.image_url} alt="Retail Offer" className="retail-offer-img" />}
    <h3>{offer.title}</h3>
    <p>{offer.description}</p>
    <p>{t('retail.price')}: {currency.symbol}{offer.price}</p>
    <p>{t('retail.discount')}: {offer.discount_value} {offer.discount_type === 'percentage' ? '%' : currency.symbol}</p>
    {offer.min_requirement && (
      <p>{t('retail.min_requirement')}: {currency.symbol}{offer.min_requirement}</p>
    )}
    <p><strong>{t('retail.total_spent')}:</strong> {currency.symbol}{spent}</p>
    <p><strong>{t('retail.available_discounts')}:</strong> {currency.symbol}{unclaimed}</p>
  </div>
);
})}
            </div>

            <RetailRecordForm
  customerId={customer.id}
  retailOffers={filteredOffers}
  setOfferData={setOfferData}
  onRecorded={(newPoints) => {
    console.log("✅ Called onRecorded with points:", newPoints);
    setTotalPoints(newPoints); // ✅ This ensures instant UI update
  }}
/>

            <RetailClaimForm
              customerId={customer.id}
              retailOffers={filteredOffers}
              offerData={offerData}
              setOfferData={setOfferData}
              onClaimed={(msg) => setMessage(msg)}
            />
          </>
        )}

       <div className="qr-code-section">
  <QRCode value={`${window.location.origin}/customer/${token}`} size={128} />
  <p className={`qr-instruction ${theme === 'dark' ? 'dark-text' : 'light-text'}`}>
    {t('scan_access_page')}
  </p>
</div>

        <CustomerReview customerId={customer.id} customerName={customer.name} coffeeShopId={customer.coffee_shop_id} />
        <ChatbotWidget coffeeShopId={coffeeShopId} />

        <button className="cta-button" onClick={() => navigate(`/customer/${token}/tombola`)}>
          {t('join_tombola')}
        </button>
        <button className="cta-button" onClick={() => navigate('/customer-reviews')}>
          {t('see_reviews')}
        </button>
      </div>

      <footer className="footer">© 2025 FlexPayz Technology</footer>
    </div>
  );
};

export default CustomerPage;
